import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import './RecommendationsAdmin.css';

function RecommendationsAdmin({ recommendations, onRefresh }) {
  const [frequencies, setFrequencies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    frequency_id: '',
    title: '',
    description: '',
    valid_from: new Date().toISOString().slice(0, 16),
    valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    priority: 10,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFrequencies();
  }, []);

  const loadFrequencies = async () => {
    try {
      const data = await apiClient.getFrequencies();
      setFrequencies(data);
    } catch (error) {
      console.error('Error loading frequencies:', error);
    }
  };

  const handleCreate = async () => {
    setError('');
    setLoading(true);

    try {
      await apiClient.createRecommendation({
        frequency_id: formData.frequency_id,
        title: formData.title,
        description: formData.description,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_until: new Date(formData.valid_until).toISOString(),
        priority: parseInt(formData.priority),
      });
      setShowModal(false);
      setFormData({
        frequency_id: '',
        title: '',
        description: '',
        valid_from: new Date().toISOString().slice(0, 16),
        valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
        priority: 10,
      });
      onRefresh();
    } catch (err) {
      setError(err.message || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recId) => {
    if (!window.confirm('Empfehlung wirklich löschen?')) return;

    try {
      await apiClient.deleteRecommendation(recId);
      onRefresh();
    } catch (error) {
      alert('Fehler beim Löschen');
    }
  };

  return (
    <div className="recommendations-admin">
      <div className="rec-header">
        <div>
          <h3>💡 Experten-Empfehlungen</h3>
          <p className="rec-hint">
            Erstelle manuelle Empfehlungen oder nutze die automatische Mondphasen-Integration
          </p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-create-rec">
          + Neue Empfehlung
        </button>
      </div>

      <div className="moon-info">
        🌙 <strong>Mondphasen-Empfehlungen:</strong> Laufen automatisch und werden angezeigt, 
        wenn keine manuellen Empfehlungen aktiv sind
      </div>

      {recommendations.length === 0 ? (
        <p className="empty-message">Noch keine manuellen Empfehlungen erstellt</p>
      ) : (
        <div className="rec-list">
          {recommendations.map((rec) => {
            const freq = frequencies.find(f => f.id === rec.frequency_id);
            const validFrom = new Date(rec.valid_from);
            const validUntil = new Date(rec.valid_until);
            const now = new Date();
            const isActive = now >= validFrom && now <= validUntil;

            return (
              <div key={rec.id} className={`rec-item ${isActive ? 'active' : 'inactive'}`}>
                <div className="rec-item-header">
                  <div className="rec-freq-badge" style={{ color: freq?.color_hex }}>
                    {freq?.icon} {freq?.name}
                  </div>
                  <span className={`rec-status-badge ${isActive ? 'active' : 'inactive'}`}>
                    {isActive ? '✅ Aktiv' : '⏳ Geplant/Abgelaufen'}
                  </span>
                </div>
                <h4 className="rec-item-title">{rec.title}</h4>
                <p className="rec-item-desc">{rec.description}</p>
                <div className="rec-item-meta">
                  <span>Von: {validFrom.toLocaleDateString('de-DE')}</span>
                  <span>Bis: {validUntil.toLocaleDateString('de-DE')}</span>
                  <span>Priorität: {rec.priority}</span>
                </div>
                <button onClick={() => handleDelete(rec.id)} className="btn-delete-rec">
                  🗑️ Löschen
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-container">
            <div className="modal-header">
              <h2 className="modal-title">Neue Empfehlung erstellen</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>

            <div className="modal-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Frequenz *</label>
                <select
                  value={formData.frequency_id}
                  onChange={(e) => setFormData({ ...formData, frequency_id: e.target.value })}
                >
                  <option value="">Wähle Frequenz...</option>
                  {frequencies.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.icon} {f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Titel *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="z.B. Winter-Balance"
                />
              </div>

              <div className="form-group">
                <label>Beschreibung *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Erkläre warum diese Frequenz jetzt empfohlen wird..."
                  rows={4}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Gültig ab</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Gültig bis</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Priorität (1-10, höher = wichtiger)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                />
              </div>

              <div className="modal-actions">
                <button onClick={() => setShowModal(false)} className="btn-modal-cancel">
                  Abbrechen
                </button>
                <button onClick={handleCreate} className="btn-modal-save" disabled={loading}>
                  {loading ? 'Erstelle...' : 'Empfehlung erstellen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecommendationsAdmin;
